import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  PaymentError,
  PaymentErrorCode,
  reportError,
} from '../payment-gateways/core/utils/errors';
import { PaymentErrorScreen } from '../payment-gateways/core/components/PaymentErrorScreen';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Route-level boundary so a bug in one gateway's adapter can't blank-screen
 * the whole payment flow — see the plan's §3. Anything that reaches here is
 * itself a bug, not a normal payment failure (those are handled explicitly
 * via PaymentError/PaymentErrorScreen without ever throwing).
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, { componentStack: info.componentStack });
  }

  render() {
    if (this.state.error) {
      return (
        <PaymentErrorScreen
          error={
            new PaymentError(PaymentErrorCode.UNKNOWN, 'Something went wrong.')
          }
          onBack={() => window.location.reload()}
        />
      );
    }

    return this.props.children;
  }
}
